import React, {useRef, useEffect} from 'react';
import { cn } from '@/utils/common_functions';

interface OtpInputProps {
  value: string;
  length?: number;
  onChange: (value: string) => void;
  error?: string;
  touched?: boolean;
  showError?: boolean;
  inputClassName?: string;
  wrapperClassName?: string;
  className?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  length = 6,
  onChange,
  inputClassName = '',
  wrapperClassName = '',
  className = '',
  onBlur,
  onFocus,
}) => {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, newValue: string) => {
    if (!/^[0-9]?$/.test(newValue)) return;
    const otpArray = value.split('');
    otpArray[index] = newValue;
    const updated = otpArray.join('').slice(0, length);
    onChange(updated);

    if (newValue && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digitsOnly = pastedData.replace(/\D/g, '').slice(0, length);

    if (digitsOnly) {
      onChange(digitsOnly);
      // Focus on the last filled input or the last input
      const focusIndex = Math.min(digitsOnly.length, length - 1);
      setTimeout(() => {
        inputsRef.current[focusIndex]?.focus();
      }, 0);
    }
  };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Shift + Backspace or Ctrl + Backspace to clear all OTP
    if ((e.shiftKey || e.ctrlKey) && e.key === 'Backspace') {
      e.preventDefault();
      onChange('');
      inputsRef.current[0]?.focus();
      return;
    }

    // Regular backspace to move to previous input
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
  };

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <div
        className={cn(
          'flex justify-center space-x-2 transition-transform duration-200',
          wrapperClassName,
        )}>
        {Array.from({length}).map((_, i) => (
          <input
            key={i}
            ref={el => (inputsRef.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[i] || ''}
            autoComplete='one-time-code'
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onBlur={handleBlur}
            onFocus={handleFocus}
            className={cn(
              'size-10 border rounded-md text-center text-[15px] focus:outline-none focus:border-ui_blue transition-all',
              inputClassName,
            )}
          />
        ))}
      </div>
    </div>
  );
};